// src/views/Tarjetas/NuevaTarjeta/TarjetaMaestra.jsx
import React from "react";
import ReactDOM from "react-dom";
import { FaSave, FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import PermisoTarjeta from "../../Tarjetas/Configuracion/PermisoTarjeta";
import EessAutocomplete from "../../../components/autocomplete/EessAutocomplete";

function TarjetaMaestra({ onClose = () => {}, onGuardar = () => {} }) {
  // ===== EE.SS. – Autocomplete (single-select) =====
  const [eessSeleccionada, setEessSeleccionada] = React.useState(null);

  // ===== Permisos (modal) =====
  const [mostrarPermisos, setMostrarPermisos] = React.useState(false);
  const [tarjetaSel, setTarjetaSel] = React.useState(null);

  // ===== Overlay (popup) =====
  // mode: "hidden" | "loading" | "success" | "error"
  const [overlay, setOverlay] = React.useState({
    open: false,
    mode: "hidden",
    msg: "",
    numero: null,
    id: null,
  });

  // ===== Observaciones =====
  const [obs, setObs] = React.useState("");

  // ===== Validación =====
  const [errors, setErrors] = React.useState({});
  const [alerta, setAlerta] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const eessSelId = eessSeleccionada?.id ? String(eessSeleccionada.id) : "";

  const validateRequired = () => {
    const newErr = {};
    if (!eessSelId) newErr.estaciones = "Debe seleccionar una EE.SS.";
    setErrors(newErr);
    setAlerta(
      Object.keys(newErr).length ? "Debes completar 1) Seleccione EE.SS." : "",
    );
    return newErr;
  };

  const requireEESSIfMissing = () => {
    if (!eessSelId) {
      setErrors((p) => ({ ...p, estaciones: "Debe seleccionar una EE.SS." }));
      setAlerta("Debes completar 1) Seleccione EE.SS.");
    }
  };

  React.useEffect(() => {
    if (errors.estaciones && eessSelId) {
      setErrors((p) => ({ ...p, estaciones: undefined }));
      setAlerta("");
    }
  }, [eessSelId, errors.estaciones]);

  const handleEessChange = (item) => {
    setEessSeleccionada(item);
    setErrors((p) => ({ ...p, estaciones: undefined }));
    setAlerta("");
  };

  // ===== Guardar (crear tarjeta maestra) =====
  const handleGuardar = async () => {
    const errs = validateRequired();
    if (Object.keys(errs).length) {
      const el = document.querySelector('[data-field="estaciones"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const payload = {
      eessId: Number(eessSeleccionada?.id),
      observaciones: obs?.trim() || null,
    };

    setSaving(true);
    setOverlay({
      open: true,
      mode: "loading",
      msg: "Generando solicitud…",
      numero: null,
      id: null,
    });

    try {
      const resp = await apiFetchJson(apiRoutes.tarjetas.solicitarMaestra, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });

      try {
        onGuardar?.(resp);
      } catch {}

      const numero =
        resp?.numeroTarjeta ??
        resp?.tarjetaNumero ??
        resp?.tarjeta ??
        resp?.numero ??
        resp?.idTarjeta ??
        resp?.data?.numeroTarjeta ??
        null;

      const idTarjeta = resp?.id ?? resp?.tarjetaId ?? resp?.data?.id ?? null;

      setOverlay({
        open: true,
        mode: "success",
        msg: numero
          ? `Tarjeta creada: ${numero}`
          : "Operación realizada correctamente.",
        numero: numero ?? null,
        id: idTarjeta ?? null,
      });

      setObs("");
      setEessSeleccionada(null);
    } catch (e) {
      console.error("[TarjetaMaestra] error POST:", e);
      setOverlay({
        open: true,
        mode: "error",
        msg: "No se pudo generar la tarjeta maestra. Intenta nuevamente.",
        numero: null,
        id: null,
      });
    } finally {
      setSaving(false);
    }
  };

  const isGuardarEnabled = React.useMemo(
    () =>
      !!eessSelId && !saving && !(overlay.open && overlay.mode === "loading"),
    [eessSelId, saving, overlay.open, overlay.mode],
  );

  const abrirPermisosConTarjetaCreada = React.useCallback(() => {
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (!mostrarPermisos) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mostrarPermisos]);

  function ModalPortal({ children }) {
    return ReactDOM.createPortal(children, document.body);
  }

  // ===== UI =====
  return (
    <div className="page">
      <div className="mb-2 flex items-start justify-between">
        <h2 className="page-title">
          Introduzca los datos de Solicitar una nueva Tarjeta
        </h2>
      </div>

      {alerta && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          {alerta}
        </div>
      )}

      {/* ===== 1) Seleccione EE.SS ===== */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        1. Seleccione EE.SS
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4">
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 mt-2">EE.SS:</label>

            <div
              className="relative w-full md:w-[520px]"
              data-field="estaciones"
            >
              <EessAutocomplete
                value={eessSeleccionada}
                onChange={handleEessChange}
                disabled={saving}
                placeholder="Código o nombre de EE.SS."
              />

              {errors.estaciones && (
                <p className="mt-1 text-xs text-red-600">{errors.estaciones}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== 2) Observaciones ===== */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        2. Observaciones
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3 text-sm md:text-base">
          <label className="text-gray-700 mt-2">Obs:</label>
          <textarea
            rows={3}
            className="border border-gray-300 rounded-md px-3 py-2 bg-white"
            style={{ textTransform: "uppercase" }}
            value={obs}
            onFocus={() => {
              requireEESSIfMissing();
            }}
            onChange={(e) => setObs(e.target.value.toUpperCase())}
            disabled={saving}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          className="btn-outline px-4 py-2"
          onClick={() => {
            onClose();
          }}
          disabled={saving}
        >
          <FaArrowLeft className="mr-2 inline" /> Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            handleGuardar();
          }}
          disabled={!isGuardarEnabled}
          className={
            isGuardarEnabled ? "btn px-4 py-2" : "btn-fil-disabled px-4 py-2"
          }
        >
          <FaSave className="mr-2 inline" /> {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>

      {/* ===== Overlay / Popup ===== */}
      {overlay.open && (
        <div className="fixed inset-0 z-[9999] bg-black/25 backdrop-blur-[1px] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl px-6 py-5 w-[90%] max-w-md text-center">
            {overlay.mode === "loading" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />
                <div className="text-gray-800 font-semibold">
                  Generando solicitud…
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Por favor, espere sin cerrar ni recargar la página.
                </div>
              </>
            )}

            {overlay.mode === "success" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-green-400 flex items-center justify-center">
                  <span className="text-green-600 text-lg">✓</span>
                </div>
                <div className="text-green-700 font-semibold">
                  {overlay.msg}
                </div>
                <div className="mt-4 flex items-center justify-center gap-2">
                  {overlay.numero && (
                    <button
                      type="button"
                      className="btn px-4 py-2"
                      onClick={abrirPermisosConTarjetaCreada}
                    >
                      Cerrar
                    </button>
                  )}
                </div>
              </>
            )}

            {overlay.mode === "error" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-red-400 flex items-center justify-center">
                  <span className="text-red-600 text-lg">!</span>
                </div>
                <div className="text-red-700 font-semibold">{overlay.msg}</div>
                <div className="mt-4">
                  <button
                    type="button"
                    className="btn px-4 py-2"
                    onClick={() =>
                      setOverlay({
                        open: false,
                        mode: "hidden",
                        msg: "",
                        numero: null,
                        id: null,
                      })
                    }
                  >
                    Entendido
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== Modal PermisoTarjeta ===== */}
      {mostrarPermisos && (
        <ModalPortal>
          <div className="fixed inset-0 z-[10000] bg-black/40">
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-[1200px] h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="text-base font-semibold text-gray-800">
                    Configuración permisos de tarjetas
                  </h3>
                  <button
                    type="button"
                    className="btn-fil"
                    onClick={() => setMostrarPermisos(false)}
                  >
                    Cerrar
                  </button>
                </div>

                <div className="h-[calc(90vh-56px)] overflow-auto">
                  <PermisoTarjeta
                    key={tarjetaSel?.numero || tarjetaSel?.id}
                    tarjetaId={tarjetaSel?.id}
                    numeroTarjeta={tarjetaSel?.numero}
                    clienteNombre={tarjetaSel?.clienteNombre}
                    chapa={tarjetaSel?.chapa}
                    vehiculoMarca={tarjetaSel?.vehiculoMarca}
                    vehiculoEtiqueta={tarjetaSel?.vehiculoEtiqueta}
                    onVolver={() => setMostrarPermisos(false)}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default TarjetaMaestra;